import { SearchIcon } from '@music-vine/cadence/icons';
import { motion } from 'motion/react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { memo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button } from '@music-vine/cadence/ui';
import { PLACEHOLDER_IMG_URL } from '@/constants/images';
import { logSearch } from '@/features/GlobalSearch/api/logging';
import { MGSearchResultCard } from '@/features/GlobalSearch/components/search/MGSearchResultCard';
import { SearchButton } from '@/features/GlobalSearch/components/search/SearchButton';
import { SearchResultsArtist } from '@/features/GlobalSearch/components/search/SearchResultsArtist';
import { SuggestedItem } from '@/features/GlobalSearch/components/search/SuggestedItem';
import { PopularSearchCarousel } from '@/features/GlobalSearch/components/shared/PopularSearchCarousel';
import { SearchCarousel } from '@/features/GlobalSearch/components/shared/SearchCarousel';
import { getTrendingResultUrl } from '@/features/GlobalSearch/utils/getTrendingSearches';
import { buildSearchUrl } from '@/features/SearchPage/utils/searchUrl';
import { mapAssetTypeToSearchTypeParam } from '@/features/GlobalSearch/utils/mapAssetTypeToSearchTypeParam';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { cn, getFirstString } from '@/lib/utils';
import { isMotionGraphicsDocument, isStockVideoDocument } from '@/utils/typeGuards';
import { ASSET_TYPE } from '@/types/asset';
import type { FormValues } from '@/features/GlobalSearch/globalSearchForm';
import type { AssetType } from '@/features/GlobalSearch/types/index';
import type { GenericSearchResult } from '@/features/SearchPage/types';
import type { SearchResponse } from '@music-vine/setlist';
const POPULAR_STOCK_VIDEO_SEARCHES = 'Popular Stock Video Searches';
const STOCK_VIDEO_ASSETS = 'Stock Video Assets';
interface ShowMoreButtonProps {
  assetType: AssetType;
  closePopup: () => void;
  onLogShowMore: () => void;
  searchTerm: string;
}
const ShowMoreButton = ({
  searchTerm,
  assetType,
  onLogShowMore,
  closePopup
}: ShowMoreButtonProps) => <Button asChild className="rounded-xl text-pink-500 hover:dark:bg-gray-800!" onClick={() => {
  onLogShowMore();
  closePopup();
}} size="sm" variant="transparent" data-sentry-element="Button" data-sentry-component="ShowMoreButton" data-sentry-source-file="SearchResults.tsx">
    <Link href={buildSearchUrl(mapAssetTypeToSearchTypeParam(assetType), searchTerm)} data-sentry-element="Link" data-sentry-source-file="SearchResults.tsx">
      Show more
    </Link>
  </Button>;
interface SearchResultsProps {
  closePopup: () => void;
  results: SearchResponse<GenericSearchResult>[] | undefined;
  searchTerm?: string;
}
export const SearchResults = memo(({
  results,
  closePopup,
  searchTerm
}: SearchResultsProps) => {
  const router = useRouter();
  const {
    getValues
  } = useFormContext<FormValues>();
  const {
    isMd
  } = useTWScreens();
  // Extract currentAssetType once at component level for performance
  const currentAssetType = getValues('assetType') ?? 'music';

  // Early return for empty results
  if (!results || results.length === 0) {
    return null;
  }

  // Use motion.div only on desktop, regular div on mobile to avoid iOS Safari touch issues
  const AnimatedContainer = isMd ? motion.div : 'div';
  const AnimatedItem = isMd ? motion.div : 'div';
  return <AnimatedContainer animate={isMd ? {
    opacity: 1
  } : undefined} className="search-results-container" initial={isMd ? {
    opacity: 0
  } : undefined} style={{
    position: 'relative'
  }} transition={isMd ? {
    opacity: {
      duration: 0.15
    }
  } : undefined}>
        {results.map((item: SearchResponse<GenericSearchResult>) => {
      if (!item.found) {
        return null;
      }
      const collectionName = item?.request_params?.collection_name as string;

      // Popular searches sections (Music, SFX, Motion, Stock Video)
      if (collectionName === 'Popular Music Searches' || collectionName === 'Popular SFX Searches' || collectionName === 'Popular Motion Graphics Searches' || collectionName === POPULAR_STOCK_VIDEO_SEARCHES) {
        let assetType: AssetType = 'motion';
        if (collectionName === 'Popular Music Searches') {
          assetType = 'music';
        } else if (collectionName === 'Popular SFX Searches') {
          assetType = 'sfx';
        } else if (collectionName === POPULAR_STOCK_VIDEO_SEARCHES) {
          assetType = ASSET_TYPE.STOCK_VIDEO;
        }
        return <div className="main-search-results my-4 px-4 md:my-0 md:mt-0! md:mb-5 md:px-0 2xl:my-6" key={collectionName}>
                <div className="mb-3">
                  <h4 className="font-semibold text-base text-gray-950 md:text-lg dark:text-white">
                    Popular searches
                  </h4>
                </div>

                {/* Desktop view */}
                <div className={cn('hidden md:flex md:gap-2', assetType === 'motion' || assetType === ASSET_TYPE.STOCK_VIDEO ? 'md:flex-col' : 'md:flex-wrap')}>
                  {(item?.hits ?? []).slice(0, 8).map((hit, idx: number) => <AnimatedItem animate={isMd ? {
              opacity: 1
            } : undefined} className="block" initial={isMd ? {
              opacity: 0
            } : undefined} key={hit.document.id} transition={isMd ? {
              duration: 0.15,
              delay: Math.min(idx * 0.02, 0.1)
            } : undefined}>
                      <SearchButton assetType={assetType} closePopup={closePopup} href={assetType === ASSET_TYPE.STOCK_VIDEO ? `/stock-video/category/${hit.document.slug ?? ''}` : getTrendingResultUrl({
                name: hit.document.name ?? '',
                type: assetType
              })} id={hit.document.id} name={hit.document.name ?? ''} onNameClick={(content: string | undefined, endSlug: string | undefined) => {
                void logSearch(searchTerm ?? '', router.asPath, endSlug ?? '', 'SUGGESTED', currentAssetType);
              }} testId={`popular-${assetType}-search`}>
                        {(assetType === 'motion' || assetType === ASSET_TYPE.STOCK_VIDEO) && <div className="flex w-full items-center gap-1">
                            <SearchIcon className="h-4 w-4 shrink-0 text-gray-700 dark:text-gray-200" />
                            <span className="truncate lowercase">
                              {hit.document.name}
                            </span>
                          </div>}
                        {assetType !== 'motion' && assetType !== ASSET_TYPE.STOCK_VIDEO && hit.document.name}
                      </SearchButton>
                    </AnimatedItem>)}
                </div>

                {/* Mobile Carousel */}
                <div className="block max-md:-mx-4 md:hidden">
                  <PopularSearchCarousel assetType={assetType} closePopup={closePopup} getHref={assetType === ASSET_TYPE.STOCK_VIDEO ? slug => `/stock-video/category/${slug}` : undefined} hits={item?.hits} onNameClick={(content: string | undefined, endSlug: string | undefined) => {
              void logSearch(searchTerm ?? '', router.asPath, endSlug ?? '', 'SUGGESTED', currentAssetType);
            }} testId={`popular-${assetType}-search`} />
                </div>
              </div>;
      }

      // Collections sections (Music, SFX)
      if (collectionName === 'Playlists' || collectionName === 'SFX Collections' || collectionName === 'Types') {
        return <div className="main-search-results my-4 mt-0! px-4 last-of-type:my-0 md:my-0 md:mb-5 md:px-0 2xl:my-6" key={collectionName}>
                <div className="mb-3 flex items-center justify-between">
                  <div style={{
              willChange: 'transform',
              transform: 'translateZ(0)',
              position: 'relative',
              zIndex: 2
            }}>
                    <h4 className="font-semibold text-base text-gray-950 md:text-lg dark:text-white">
                      {(() => {
                  if (collectionName === 'SFX Collections') {
                    return 'Collections';
                  }
                  return collectionName;
                })()}
                    </h4>
                  </div>
                </div>

                {collectionName === 'SFX Collections' ? <div className="grid grid-cols-2 gap-3 md:hidden">
                    {item.hits?.slice(0, 6).map((hit, idx: number) => {
              if (!(hit.document.name && hit.document.slug)) {
                return null;
              }
              const tileImage = getFirstString(hit.document.image, PLACEHOLDER_IMG_URL);
              return <AnimatedItem animate={isMd ? {
                opacity: 1
              } : undefined} initial={isMd ? {
                opacity: 0
              } : undefined} key={`sfx-collection-${hit.document.id}`} transition={isMd ? {
                duration: 0.2,
                delay: Math.min(idx * 0.05, 0.25)
              } : undefined}>
                          <div>
                            <Link className="relative block aspect-square overflow-hidden rounded-lg" href={hit.document.slug} onClick={() => {
                    closePopup();
                    void logSearch(searchTerm ?? '', router.asPath, hit.document.slug ?? '', 'SUGGESTED', currentAssetType);
                  }}>
                              <Image alt={hit.document.name} className="object-cover" fill src={tileImage} />
                            </Link>
                            <div className="mt-2">
                              <h3 className="line-clamp-2 font-sans font-semibold text-gray-950 text-sm dark:text-white">
                                {hit.document.name}
                              </h3>
                            </div>
                          </div>
                        </AnimatedItem>;
            })}
                  </div> : null}

                <div className={cn(collectionName === 'SFX Collections' ? 'hidden md:block' : 'block')}>
                  <SearchCarousel assetType={collectionName === 'Types' ? 'motion' : collectionName === 'SFX Collections' ? 'sfx' : 'music'} closePopup={closePopup} data={item.hits} onTileClick={(endSlug: string) => {
              void logSearch(searchTerm ?? '', router.asPath, endSlug ?? '', 'SUGGESTED', currentAssetType);
            }} />
                </div>
              </div>;
      }

      // Artists section
      if (collectionName === 'Artists') {
        return <div className="main-search-results artists my-4 px-4 last-of-type:my-0 md:my-0 md:mb-5 md:px-0 2xl:my-6" key={collectionName}>
                <div className="mb-3">
                  <h4 className="font-semibold text-base text-gray-950 md:text-lg dark:text-white">
                    {collectionName}
                  </h4>
                </div>
                <AnimatedItem className="flex flex-col gap-4">
                  {(item?.hits ?? []).slice(0, 8).map((hit, idx: number) => <AnimatedItem animate={isMd ? {
              opacity: 1,
              y: 0
            } : undefined} className={cn(idx >= 3 && 'hidden md:block')} initial={isMd ? {
              opacity: 0,
              y: 10
            } : undefined} key={hit.document.id} transition={isMd ? {
              duration: 0.3,
              delay: idx * 0.1
            } : undefined}>
                      <SearchResultsArtist closePopup={closePopup} item={{
                ...hit.document,
                type: collectionName
              }} searchTerm={searchTerm} searchType="SUGGESTED" />
                    </AnimatedItem>)}
                </AnimatedItem>
              </div>;
      }

      // LUTs assets section
      if (collectionName === 'LUTs Assets') {
        return <div className="main-search-results mb-5 max-md:px-4" key={collectionName}>
                <div className="mb-3 flex items-center justify-between">
                  <div style={{
              willChange: 'transform',
              transform: 'translateZ(0)',
              position: 'relative',
              zIndex: 2
            }}>
                    <h4 className="font-semibold text-base text-gray-950 md:text-lg dark:text-white">
                      LUTs
                    </h4>
                  </div>

                  {Boolean(searchTerm?.trim()) && <ShowMoreButton assetType="luts" closePopup={closePopup} onLogShowMore={() => {
              void logSearch(searchTerm ?? '', router.asPath, buildSearchUrl('luts', searchTerm ?? ''), 'SHOW_MORE', currentAssetType);
            }} searchTerm={searchTerm!} />}
                </div>

                <AnimatedItem className="grid grid-cols-1 gap-x-3 gap-y-4 md:grid-cols-2">
                  {(item.hits ?? [])?.slice(0, 4).map((hit, idx: number) => {
              if (!isMotionGraphicsDocument(hit.document)) {
                return null;
              }
              const lut = hit.document;
              return <AnimatedItem animate={isMd ? {
                opacity: 1
              } : undefined} initial={isMd ? {
                opacity: 0
              } : undefined} key={lut.id} transition={isMd ? {
                duration: 0.2,
                delay: Math.min(idx * 0.05, 0.25)
              } : undefined}>
                          <MGSearchResultCard compatibility={lut.compatibility} contributor={lut.contributor.name} isLut isNew={false} name={lut.name ?? ''} onClick={(slug: string) => {
                  void logSearch(searchTerm ?? '', router.asPath, slug, 'SUGGESTED', currentAssetType);
                }} posterUrl={lut.image ?? ''} previewVideoUrl={lut.preview_video_url} slug={lut.slug} />
                        </AnimatedItem>;
            }).filter(Boolean)}
                </AnimatedItem>
              </div>;
      }

      // Motion assets section
      if (collectionName === 'Motion Assets') {
        return <div className="main-search-results mb-5 max-md:px-4" key={collectionName}>
                <div className="mb-3 flex items-center justify-between">
                  <div style={{
              willChange: 'transform',
              transform: 'translateZ(0)',
              position: 'relative',
              zIndex: 2
            }}>
                    <h4 className="font-semibold text-base text-gray-950 md:text-lg dark:text-white">
                      Motion assets
                    </h4>
                  </div>

                  {Boolean(searchTerm?.trim()) && <ShowMoreButton assetType="motion" closePopup={closePopup} onLogShowMore={() => {
              void logSearch(searchTerm ?? '', router.asPath, buildSearchUrl(mapAssetTypeToSearchTypeParam(currentAssetType), searchTerm ?? ''), 'SHOW_MORE', currentAssetType);
            }} searchTerm={searchTerm!} />}
                </div>

                <AnimatedItem className="grid grid-cols-1 gap-x-3 gap-y-4 md:grid-cols-2">
                  {(item.hits ?? [])?.slice(0, 4).map((hit, idx: number) => {
              // Type-safe check for motion graphics properties
              if (!isMotionGraphicsDocument(hit.document)) {
                return null;
              }
              const motionGraphic = hit.document;
              return <AnimatedItem animate={isMd ? {
                opacity: 1
              } : undefined} initial={isMd ? {
                opacity: 0
              } : undefined} key={motionGraphic.id} transition={isMd ? {
                duration: 0.2,
                delay: Math.min(idx * 0.05, 0.25)
              } : undefined}>
                          <MGSearchResultCard compatibility={motionGraphic.compatibility} contributor={motionGraphic.contributor.name} isLut={motionGraphic.is_lut} isNew={false} name={motionGraphic.name ?? ''} onClick={(slug: string) => {
                  void logSearch(searchTerm ?? '', router.asPath, slug, 'SUGGESTED', currentAssetType);
                }} posterUrl={motionGraphic.image ?? ''} previewVideoUrl={motionGraphic.preview_video_url} slug={motionGraphic.slug} />
                        </AnimatedItem>;
            }).filter(Boolean)}
                </AnimatedItem>
              </div>;
      }

      // Stock video assets section
      if (collectionName === STOCK_VIDEO_ASSETS) {
        return <div className="main-search-results mb-5 max-md:px-4" key={collectionName}>
                <div className="mb-3 flex items-center justify-between">
                  <div style={{
              willChange: 'transform',
              transform: 'translateZ(0)',
              position: 'relative',
              zIndex: 2
            }}>
                    <h4 className="font-semibold text-base text-gray-950 md:text-lg dark:text-white">
                      Stock video
                    </h4>
                  </div>

                  {Boolean(searchTerm?.trim()) && <ShowMoreButton assetType={ASSET_TYPE.STOCK_VIDEO} closePopup={closePopup} onLogShowMore={() => {
              void logSearch(searchTerm ?? '', router.asPath, buildSearchUrl(mapAssetTypeToSearchTypeParam(currentAssetType), searchTerm ?? ''), 'SHOW_MORE', currentAssetType);
            }} searchTerm={searchTerm!} />}
                </div>

                <AnimatedItem className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {(item.hits ?? [])?.slice(0, 4).map((hit, idx: number) => {
              // Type-safe check for stock video properties
              if (!isStockVideoDocument(hit.document)) {
                return null;
              }
              const stockVideo = hit.document;
              const title = stockVideo.title ?? stockVideo.name ?? '';
              const thumbnailUrl = getFirstString(stockVideo.thumbnail_url, PLACEHOLDER_IMG_URL);
              return <AnimatedItem animate={isMd ? {
                opacity: 1
              } : undefined} initial={isMd ? {
                opacity: 0
              } : undefined} key={stockVideo.id} transition={isMd ? {
                duration: 0.2,
                delay: Math.min(idx * 0.05, 0.25)
              } : undefined}>
                          <div className="-m-2 flex flex-col gap-1 rounded-xl p-2 transition-all duration-300 hover:bg-gray-100 dark:hover:bg-gray-950">
                            <Link className="relative block aspect-video overflow-hidden rounded-lg" href={`/stock-video/asset/${stockVideo.slug}`} onClick={() => {
                    closePopup();
                    void logSearch(searchTerm ?? '', router.asPath, stockVideo.slug ?? '', 'SUGGESTED', currentAssetType);
                  }}>
                              <Image alt={title} className="object-cover" fill src={thumbnailUrl} />
                            </Link>
                            <div className="mt-2">
                              <h3 className="line-clamp-1 font-sans font-semibold text-gray-950 text-sm dark:text-white">
                                {title}
                              </h3>
                              {stockVideo.contributor_slug ? <Link href={`/contributors/${stockVideo.contributor_slug}?type=stock-video`} onClick={() => {
                      closePopup();
                      void logSearch(searchTerm ?? '', router.asPath, stockVideo.contributor_slug ?? '', 'SUGGESTED', currentAssetType);
                    }} className="text-gray-700 text-xs hover:underline dark:text-gray-200">
                                  {stockVideo.contributor_name}
                                </Link> : <p className="text-gray-700 text-xs dark:text-gray-200">
                                  {stockVideo.contributor_name}
                                </p>}
                            </div>
                          </div>
                        </AnimatedItem>;
            }).filter(Boolean)}
                </AnimatedItem>
              </div>;
      }

      // Standard results section (Tracks, SFX)
      return <div className="main-search-results standard my-4 last-of-type:my-0 max-md:px-4 md:my-0 md:mb-0 2xl:my-6 2xl:mb-0" key={collectionName}>
              <div className="mb-3 flex items-center justify-between">
                <h4 className="font-semibold text-base text-gray-950 md:text-lg dark:text-white">
                  {collectionName === 'SFX' ? 'Sound effects' : collectionName}
                </h4>

                {Boolean(searchTerm) && !['Playlists', 'SFX Collections', 'Types'].includes(collectionName) && <ShowMoreButton assetType={collectionName === 'SFX' ? 'sfx' : 'music'} closePopup={closePopup} onLogShowMore={() => {
            void logSearch(searchTerm ?? '', router.asPath, buildSearchUrl(mapAssetTypeToSearchTypeParam(currentAssetType), searchTerm ?? ''), 'SHOW_MORE', currentAssetType);
          }} searchTerm={searchTerm!} />}
              </div>
              <AnimatedItem className="flex list-none flex-col gap-4">
                {item?.hits?.map((hit, idx: number) => {
            if (idx >= 8) {
              return null;
            }
            return <AnimatedItem animate={isMd ? {
              opacity: 1
            } : undefined} className={cn('block', {
              'hidden md:block': idx >= 6
            })} initial={isMd ? {
              opacity: 0
            } : undefined} key={hit.document.id} transition={isMd ? {
              duration: 0.3,
              delay: idx * 0.08
            } : undefined}>
                      <SuggestedItem closePopup={closePopup} item={{
                ...hit.document,
                type: collectionName
              }} searchTerm={searchTerm} searchType="SUGGESTED" type="search" />
                    </AnimatedItem>;
          })}
              </AnimatedItem>
            </div>;
    })}
      </AnimatedContainer>;
});
SearchResults.displayName = 'SearchResults';